// src/ui/Chat.tsx
import { h } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import { Form, FormField } from "./components/Form";
import { useForm } from "./hooks/useForm";

interface Message {
    id: string;
    sender: "User" | "Agent";
    text: string;
    timestamp: number;
}

interface ChatFormValues {
    message: string;
}

interface AgentResponse {
    success: boolean;
    reply?: string;
    error?: string;
}

export default function Chat() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const {
        values,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
        resetForm,
    } = useForm<ChatFormValues>({
        initialValues: {
            message: "",
        },
        validate: (values: ChatFormValues) => {
            const errors: Partial<Record<keyof ChatFormValues, string>> = {};
            if (!values.message.trim()) {
                errors.message = "Message cannot be empty";
            }
            if (values.message.length > 1000) {
                errors.message = "Message is too long (max 1000 characters)";
            }
            return errors;
        },
        onSubmit: async (values: ChatFormValues) => {
            const userMessage: Message = {
                id: crypto.randomUUID(),
                sender: "User",
                text: values.message.trim(),
                timestamp: Date.now(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setError(null);
            resetForm();

            try {
                const response = await fetch("/agent/process", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ message: values.message }),
                });

                const data = (await response.json()) as AgentResponse;

                if (!response.ok || !data.success) {
                    throw new Error(
                        data.error || "Failed to get response from agent",
                    );
                }

                if (data.reply) {
                    const agentMessage: Message = {
                        id: crypto.randomUUID(),
                        sender: "Agent",
                        text: data.reply,
                        timestamp: Date.now(),
                    };
                    setMessages((prev) => [...prev, agentMessage]);
                }
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to process message",
                );
                console.error("Chat error:", err);
            }
        },
    });

    return (
        <div className="chat-container">
            <div className="messages-container">
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`message ${message.sender.toLowerCase()}-message`}
                    >
                        <div className="message-header">
                            <span className="sender">{message.sender}</span>
                            <span className="timestamp">
                                {new Date(
                                    message.timestamp,
                                ).toLocaleTimeString()}
                            </span>
                        </div>
                        <div className="message-content">{message.text}</div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {error && (
                <div className="error-message" role="alert">
                    {error}
                </div>
            )}

            <Form handleSubmit={handleSubmit} className="chat-form">
                <div className="input-container">
                    <FormField
                        label="Message"
                        name="message"
                        value={values.message}
                        onChange={handleChange("message")}
                        error={errors.message}
                        required
                    />
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="send-button"
                        aria-label={
                            isSubmitting ? "Sending message..." : "Send message"
                        }
                    >
                        {isSubmitting ? "Sending..." : "Send"}
                    </button>
                </div>
            </Form>
        </div>
    );
}
